import React from 'react';
import { HiOutlineX } from '../icons';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  children: React.ReactNode;
}

export interface DialogHeaderProps extends React.ComponentProps<'div'> {
  children: React.ReactNode;
}

export interface DialogBodyProps extends React.ComponentProps<'div'> {
  children: React.ReactNode;
}

export interface DialogFooterProps extends React.ComponentProps<'div'> {
  children: React.ReactNode;
}

const Dialog = React.forwardRef<React.ElementRef<'div'>, DialogProps>(
  (
    {
      open,
      onClose,
      size = 'md',
      closeOnBackdrop = true,
      closeOnEscape = true,
      showCloseButton = true,
      children,
    },
    ref
  ) => {
    const dialogRef = React.useRef<React.ElementRef<'div'>>(null);

    React.useImperativeHandle(ref, () => dialogRef.current!);

    // Handle escape key
    React.useEffect(() => {
      if (!closeOnEscape) return;

      const handleEscape = (e: globalThis.KeyboardEvent) => {
        if (e.key === 'Escape' && open) {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [open, onClose, closeOnEscape]);

    // Handle body scroll lock
    React.useEffect(() => {
      if (open) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }

      return () => {
        document.body.style.overflow = 'unset';
      };
    }, [open]);

    // Handle backdrop click
    const handleBackdropClick = (e: React.MouseEvent) => {
      if (closeOnBackdrop && e.target === e.currentTarget) {
        onClose();
      }
    };

    if (!open) return null;

    const sizeClasses = {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      full: 'max-w-full mx-4',
    };

    return (
      <div
        className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
        onClick={handleBackdropClick}
        onKeyDown={e => {
          if (e.key === 'Escape' && closeOnEscape) {
            onClose();
          }
        }}
        role="presentation"
      >
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

        {/* Dialog */}
        <div
          ref={dialogRef}
          className={`relative bg-white dark:bg-xtnd-dark-800 rounded-lg shadow-xl w-full ${sizeClasses[size]} transform transition-all my-8 max-h-[calc(100vh-4rem)] flex flex-col`}
          role="dialog"
          aria-modal="true"
        >
          {showCloseButton && (
            <button
              type="button"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              onClick={onClose}
            >
              <HiOutlineX className="h-6 w-6" />
            </button>
          )}

          {children}
        </div>
      </div>
    );
  }
);

const DialogHeader = React.forwardRef<React.ElementRef<'div'>, DialogHeaderProps>(
  ({ className = '', children, ...props }, ref) => {
    const classes =
      `px-6 py-4 border-b border-gray-200 dark:border-xtnd-dark-600 flex-shrink-0 ${className}`.trim();

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

const DialogBody = React.forwardRef<React.ElementRef<'div'>, DialogBodyProps>(
  ({ className = '', children, ...props }, ref) => {
    const classes = `px-6 py-4 flex-1 overflow-y-auto ${className}`.trim();

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

const DialogFooter = React.forwardRef<React.ElementRef<'div'>, DialogFooterProps>(
  ({ className = '', children, ...props }, ref) => {
    const classes =
      `px-6 py-4 border-t border-gray-200 dark:border-xtnd-dark-600 flex justify-end space-x-2 flex-shrink-0 ${className}`.trim();

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

Dialog.displayName = 'Dialog';
DialogHeader.displayName = 'DialogHeader';
DialogBody.displayName = 'DialogBody';
DialogFooter.displayName = 'DialogFooter';

export default Dialog;
export { DialogHeader, DialogBody, DialogFooter };
